/* global BigInt */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CountTrend } from './TrendPanel';

function purchasedSignatureSeries(productSeries, domainSeries = []) {
  const normalized = (Array.isArray(productSeries) ? productSeries : []).map(bucket => {
    const totals = { qualified: 0n, simple: 0n };
    (Array.isArray(bucket.product_groups) ? bucket.product_groups : []).forEach(group => {
      const key = String(group.key || group.name || '').toLocaleLowerCase();
      const target = key === 'qualified-signatures'
        ? 'qualified'
        : key === 'simple-signatures' ? 'simple' : null;
      if (!target) return;
      (Array.isArray(group.products) ? group.products : []).forEach(product => {
        const value = product && product.fulfilled_quantity
          ? product.fulfilled_quantity.purchased
          : null;
        if (/^\d+$/.test(String(value))) totals[target] += BigInt(value);
      });
    });
    return {
      ...bucket,
      purchased_signatures: {
        qualified: String(totals.qualified),
        simple: String(totals.simple)
      }
    };
  });
  const domain = Array.isArray(domainSeries) ? domainSeries : [];
  if (domain.length === 0) return normalized;
  const byIdentity = new Map();
  normalized.forEach(bucket => {
    if (bucket.key) byIdentity.set(`key:${bucket.key}`, bucket);
    if (bucket.date_from) byIdentity.set(`date:${bucket.date_from}`, bucket);
  });
  return domain.map(bucket => {
    const existing = byIdentity.get(`key:${bucket.key}`)
      || byIdentity.get(`date:${bucket.date_from}`);
    return existing ? {
      ...existing,
      date_from: bucket.date_from || existing.date_from,
      date_to: bucket.date_to || existing.date_to,
      key: bucket.key || existing.key,
      label: bucket.label || existing.label
    } : {
      ...bucket,
      purchased_signatures: { qualified: '0', simple: '0' }
    };
  });
}

const TREND_DEFINITIONS = [
  {
    color: '#5b57dc',
    key: 'purchased-signatures',
    secondary: {
      color: '#36a27f',
      labelId: 'components.pages.reports.periods.overview.purchasedSimple'
    },
    source: 'purchases',
    seriesLabelId: 'components.pages.reports.periods.overview.purchasedQualified',
    titleId: 'components.pages.reports.periods.overview.purchasedSignatures',
    value: bucket => bucket.purchased_signatures && bucket.purchased_signatures.qualified
  },
  {
    color: '#13735b',
    key: 'qualified-signatures',
    source: 'activity',
    titleId: 'components.pages.reports.periods.overview.qualifiedSignatures',
    value: bucket => bucket.signatures_used && bucket.signatures_used.qualified
  },
  {
    color: '#36a27f',
    key: 'simple-signatures',
    source: 'activity',
    titleId: 'components.pages.reports.periods.overview.simpleSignatures',
    value: bucket => bucket.signatures_used && bucket.signatures_used.simple
  },
  {
    color: '#b86616',
    key: 'signed-packages',
    source: 'activity',
    titleId: 'components.pages.reports.periods.overview.signedPackages',
    value: bucket => bucket.packages_signed && bucket.packages_signed.total
  }
];

function RemoteTrendState({ error, loading, source }) {
  const prefix = source === 'purchases'
    ? 'components.pages.reports.periods.overview.purchases'
    : 'components.pages.reports.periods.overview';
  if (loading) {
    return <div aria-live="polite" className="report-period-overview__state" role="status"><FormattedMessage id={`${prefix}.loading`}/></div>;
  }
  if (error) {
    return <div className="report-period-overview__state report-period-overview__state--error" role="alert"><FormattedMessage id={`${prefix}.error`}/></div>;
  }
  return null;
}

function PeriodTrendsOverview({ activityData, activityError, activityLoading, periodData, periodPreset, purchaseData, purchaseError, purchaseLoading }) {
  const intl = useIntl();
  const periodSeries = Array.isArray(periodData && periodData.series) ? periodData.series : [];
  const activitySeries = Array.isArray(activityData && activityData.series) ? activityData.series : [];
  const purchaseSeries = purchasedSignatureSeries(
    purchaseData && purchaseData.product_series && purchaseData.product_series.current,
    periodSeries
  );
  const periodMeta = (periodData && periodData.meta) || {};
  const activityMeta = (activityData && activityData.meta) || {};
  const purchaseMeta = (purchaseData && purchaseData.meta) || {};

  return (
    <section aria-labelledby="period-trends-overview-title" className="report-period-overview">
      <header>
        <h2 id="period-trends-overview-title"><FormattedMessage id="components.pages.reports.periods.overview.title"/></h2>
        <p><FormattedMessage id="components.pages.reports.periods.overview.hint"/></p>
      </header>
      <div className="report-period-overview__grid">
        {TREND_DEFINITIONS.map(definition => {
          const sourceLoading = definition.source === 'purchases' ? purchaseLoading : activityLoading;
          const sourceError = definition.source === 'purchases' ? purchaseError : activityError;
          const hasRemoteState = definition.source !== 'periods' && (sourceLoading || sourceError);
          const remoteState = hasRemoteState
            ? <RemoteTrendState error={sourceError} loading={sourceLoading} source={definition.source}/>
            : null;
          const series = definition.source === 'purchases'
            ? purchaseSeries
            : definition.source === 'activity' ? activitySeries : periodSeries;
          return (
            <article
              aria-label={intl.formatMessage({ id: definition.titleId })}
              className="report-period-overview__card"
              data-testid={`period-overview-${definition.key}`}
              key={definition.key}
            >
              {remoteState || (
                <CountTrend
                  annual={periodPreset === 'ytd'}
                  color={definition.color}
                  currentPeriod={definition.source === 'activity'
                    ? activityMeta.period
                    : definition.source === 'purchases' ? purchaseMeta.period : periodMeta.period}
                  secondary={definition.secondary && {
                    ...definition.secondary,
                    valueAccessor: bucket => bucket.purchased_signatures && bucket.purchased_signatures.simple
                  }}
                  series={series}
                  seriesLabelId={definition.seriesLabelId}
                  testId={`period-overview-chart-${definition.key}`}
                  titleId={definition.titleId}
                  valueAccessor={definition.value}
                />
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export { PeriodTrendsOverview, purchasedSignatureSeries, TREND_DEFINITIONS };
